// sketch_classifier_web.js - ONNX model inference for web browsers
// Requires: onnxruntime-web (npm install onnxruntime-web)

import * as ort from 'onnxruntime-web';
class WebSketchClassifier {
    constructor() {
        this.session = null;
        this.classLabels = [
            'ant', 'apple', 'arm', 'axe', 'banana', 'basket', 'bear', 'bed', 'bee', 'bench', 
            'bicycle', 'bird', 'book', 'bread', 'bridge', 'bus', 'butterfly', 'cactus', 'cake', 
            'calculator', 'camel', 'candle', 'car', 'carrot', 'castle', 'cat', 'chair', 'church', 
            'cloud', 'cow', 'crab', 'crocodile', 'crown', 'cup', 'dog', 'door', 'ear', 'elephant', 
            'eye', 'face', 'fan', 'feather', 'fish', 'foot', 'frog', 'giraffe', 'hat', 'helicopter', 
            'helmet', 'horse', 'house', 'kangaroo', 'knife', 'ladder', 'leaf', 'lion', 'lobster', 
            'monkey', 'moon', 'mosquito', 'mouth', 'mug', 'nose', 'owl', 'parrot', 'pig', 'pineapple', 
            'pizza', 'rabbit', 'radio', 'scissors', 'screwdriver', 'sheep', 'shoe', 'snail', 'snake', 
            'spider', 'spoon', 'squirrel', 'sun', 'swan', 'syringe', 'telephone', 'television', 'tent', 
            'tiger', 'toilet', 'tooth', 'toothbrush', 'tractor', 'train', 'tree', 'truck', 'trumpet', 
            'umbrella', 'van', 'violin', 'wheel', 'windmill', 'zebra'
        ];
    }

    async loadModelFromBlob(modelBlob) {
        try {
            // Convert blob to ArrayBuffer
            const modelBuffer = await modelBlob.arrayBuffer();
            
            // Create ONNX session from buffer
            this.session = await ort.InferenceSession.create(modelBuffer);
            console.log('Model loaded successfully from blob');
            return this.session;
        } catch (error) {
            console.error('Error loading model from blob:', error);
            throw error;
        }
    }

    async preprocessImageFromBlob(imageBlob) {
        return new Promise((resolve, reject) => {
            // Create image element from blob
            const img = new Image();
            const url = URL.createObjectURL(imageBlob);
            
            img.onload = () => {
                try {
                    // Create canvas for preprocessing
                    const canvas = document.createElement('canvas');
                    canvas.width = 224;
                    canvas.height = 224;
                    const ctx = canvas.getContext('2d');
                    
                    // Fill with white background (padding effect)
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, 224, 224);
                    
                    // Calculate scaling to maintain aspect ratio with padding
                    const scale = Math.min(204 / img.width, 204 / img.height); // 204 = 224 - 20 (10px padding each side)
                    const scaledWidth = img.width * scale;
                    const scaledHeight = img.height * scale;
                    
                    // Center the image
                    const x = (224 - scaledWidth) / 2;
                    const y = (224 - scaledHeight) / 2;
                    
                    // Draw the image
                    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
                    
                    // Get image data and convert to grayscale, then to RGB
                    const imageData = ctx.getImageData(0, 0, 224, 224);
                    const data = imageData.data;
                    
                    // Convert to Float32Array and normalize
                    const inputArray = new Float32Array(3 * 224 * 224);
                    let idx = 0;
                    
                    for (let i = 0; i < data.length; i += 4) {
                        // Convert RGBA to grayscale
                        const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
                        
                        // Normalize to [-1, 1] range (mean=0.5, std=0.5)
                        const normalized = (gray / 255.0 - 0.5) / 0.5;
                        
                        // Replicate across 3 channels (RGB)
                        inputArray[idx] = normalized;                    // R
                        inputArray[idx + 224 * 224] = normalized;        // G
                        inputArray[idx + 2 * 224 * 224] = normalized;    // B
                        idx++;
                    }
                    
                    // Clean up object URL
                    URL.revokeObjectURL(url);
                    resolve(inputArray);
                } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            };
            
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load image from blob'));
            };
            
            img.src = url;
        });
    }

    async predict(imageBlob, topK = 5) {
        if (!this.session) {
            throw new Error('Model not loaded. Call loadModelFromBlob() first.');
        }

        try {
            // Preprocess the image
            const inputTensor = await this.preprocessImageFromBlob(imageBlob);
            
            // Create input tensor for ONNX
            const tensor = new ort.Tensor('float32', inputTensor, [1, 3, 224, 224]);
            
            // Run inference
            const feeds = { [this.session.inputNames[0]]: tensor };
            const results = await this.session.run(feeds);
            
            // Get output tensor
            const output = results[this.session.outputNames[0]];
            const predictions = Array.from(output.data);
            
            // Get top-K predictions
            const indexed = predictions.map((score, index) => ({ score, index }));
            indexed.sort((a, b) => b.score - a.score);
            
            const topPredictions = indexed.slice(0, topK).map(item => ({
                label: this.classLabels[item.index],
                confidence: this.softmax(predictions)[item.index],
                score: item.score
            }));
            
            return topPredictions;
            
        } catch (error) {
            console.error('Error during prediction:', error);
            throw error;
        }
    }

    async predictWithComparison(imageBlob, baseWord, topK = 5) {
        const predictions = await this.predict(imageBlob, topK);
        
        // Find if baseWord matches any prediction
        const baseWordLower = baseWord.toLowerCase();
        const matchingPrediction = predictions.find(pred => 
            pred.label.toLowerCase() === baseWordLower
        );
        
        const result = {
            predictions: predictions,
            baseWord: baseWord,
            isMatch: !!matchingPrediction,
            matchConfidence: matchingPrediction ? matchingPrediction.confidence : 0,
            topPrediction: predictions[0]
        };
        
        return result;
    }

    softmax(arr) {
        const max = Math.max(...arr);
        const exps = arr.map(x => Math.exp(x - max));
        const sum = exps.reduce((a, b) => a + b, 0);
        return exps.map(x => x / sum);
    }

    getSupportedLabels() {
        return [...this.classLabels];
    }
}

// Exported function for use in other files
async function classifySketch(modelBlob, imageBlob, baseWord, options = {}) {
    const {
        topK = 5,
        returnComparison = true
    } = options;
    
    try {
        const classifier = new WebSketchClassifier();
        await classifier.loadModelFromBlob(modelBlob);
        
        if (baseWord && returnComparison) {
            return await classifier.predictWithComparison(imageBlob, baseWord, topK);
        } else {
            const predictions = await classifier.predict(imageBlob, topK);
            return {
                predictions: predictions,
                topPrediction: predictions[0]
            };
        }
    } catch (error) {
        console.error('Error in classifySketch:', error);
        throw error;
    }
}
// ES Module exports (for import/export syntax)
export { WebSketchClassifier, classifySketch};

// Default export
export default classifySketch;